/**
 * Points table. Stored per competition (or per season) so the table can change
 * between seasons without rewriting history: a prediction is always scored
 * with the rules version attached to its match.
 *
 * The result component uses the HIGHEST tier that applies (they don't add up):
 *   exact score  >  correct goal difference  >  correct outcome  >  nothing
 * The two bonuses add on top.
 */
export type ResultBasis = 'REGULAR_TIME' | 'AFTER_EXTRA_TIME';

export interface ScoringRules {
  /**
   * Which score predictions are judged on when a knockout match goes to extra time.
   * AFTER_EXTRA_TIME (the convention of most public predictor games) uses the
   * 120-minute score; REGULAR_TIME (the betting convention) uses the 90-minute
   * score. Shoot-out goals never count either way, so a shoot-out is a draw.
   * League matches are unaffected.
   */
  resultBasis: ResultBasis;
  /** Exact score, e.g. predicted 2-1, finished 2-1. */
  exactScore: number;
  /**
   * Right outcome AND right goal difference but not exact (2-1 vs 3-2, 1-1 vs 2-2).
   * Set to 0 to disable the tier, in which case these fall through to `correctOutcome`.
   */
  goalDifference: number;
  /** Right outcome only (home win / draw / away win). */
  correctOutcome: number;
  /** Bonus: right half-time outcome. */
  halfTimeOutcome: number;
  /** Bonus: right first goalscorer (own goals don't count; see facts.ts). */
  firstScorer: number;
}

export const DEFAULT_RULES: ScoringRules = {
  resultBasis: 'AFTER_EXTRA_TIME',
  exactScore: 5,
  goalDifference: 3,
  correctOutcome: 2,
  halfTimeOutcome: 1,
  firstScorer: 2,
};

/**
 * Structural check: what the scorer needs to run at all. `rescoreMatch` refuses
 * rules that fail it (SQL casts values to int, so a fractional value would make
 * SQL and TypeScript disagree).
 */
export function checkRulesShape(r: ScoringRules): string[] {
  const errors: string[] = [];
  if (r.resultBasis !== 'REGULAR_TIME' && r.resultBasis !== 'AFTER_EXTRA_TIME') errors.push('resultBasis is invalid');
  const points = [r.exactScore, r.goalDifference, r.correctOutcome, r.halfTimeOutcome, r.firstScorer];
  const names = ['exactScore', 'goalDifference', 'correctOutcome', 'halfTimeOutcome', 'firstScorer'];
  points.forEach((v, i) => {
    if (!Number.isInteger(v) || v < 0 || v > 1000) errors.push(`${names[i]} must be an integer from 0 to 1000`);
  });
  return errors;
}

/**
 * Full check, run by the admin console before a points table is saved:
 * the shape, plus the design rules of the game.
 */
export function validateRules(r: ScoringRules): string[] {
  const errors = checkRulesShape(r);
  if (r.exactScore <= r.correctOutcome) errors.push('exactScore must be greater than correctOutcome');
  if (r.goalDifference !== 0 && (r.goalDifference <= r.correctOutcome || r.goalDifference >= r.exactScore)) {
    errors.push('goalDifference must sit strictly between correctOutcome and exactScore (or be 0)');
  }
  if (r.halfTimeOutcome + r.firstScorer >= r.exactScore) {
    errors.push('bonuses together must be worth less than an exact score');
  }
  return errors;
}
